import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Stack, TextField, Typography, Chip, alpha,
} from '@mui/material';
import { MyLocation, Place } from '@mui/icons-material';
import ZoneMapPanel from '../maps/ZoneMapPanel';
import LocationSearchField from '../maps/LocationSearchField';
import { searchLocations, type LocationResult } from '../../services/locationService';

interface RestaurantLocationPickerProps {
  latitude: string;
  longitude: string;
  addressLine?: string;
  city?: string;
  onChange: (lat: string, lng: string) => void;
  onAddressSuggest?: (parts: { addressLine?: string; city?: string; state?: string; pincode?: string }) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

function parseCoord(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function RestaurantLocationPicker({
  latitude,
  longitude,
  addressLine = '',
  city = '',
  onChange,
  onAddressSuggest,
  required,
  error,
  helperText,
}: RestaurantLocationPickerProps) {
  const lat = parseCoord(latitude);
  const lng = parseCoord(longitude);
  const hasCoords = lat != null && lng != null;
  const [searchValue, setSearchValue] = useState<LocationResult | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');

  const center = useMemo(
    () => (hasCoords ? { lat: lat!, lng: lng! } : undefined),
    [hasCoords, lat, lng],
  );

  const setCoords = (nextLat: number, nextLng: number) => {
    onChange(nextLat.toFixed(6), nextLng.toFixed(6));
    setLocateError('');
  };

  const applyLocation = (loc: LocationResult) => {
    setSearchValue(loc);
    setCoords(loc.lat, loc.lng);
    onAddressSuggest?.({
      addressLine: loc.address || loc.label,
      city: loc.city,
      state: loc.state,
      pincode: loc.postalCode,
    });
  };

  const locateFromAddress = async () => {
    const query = [addressLine, city].map((p) => p.trim()).filter(Boolean).join(', ');
    if (query.length < 3) {
      setLocateError('Enter address or city first, then locate on the map');
      return;
    }
    setLocating(true);
    setLocateError('');
    try {
      const results = await searchLocations(query, { city: city || undefined, limit: 1 });
      if (!results.length) {
        setLocateError('No map match found — search below or click the map to pin manually');
        return;
      }
      applyLocation(results[0]);
    } catch {
      setLocateError('Could not look up that address — pin the map manually or try search');
    } finally {
      setLocating(false);
    }
  };

  // When address/city become available and no pin yet, try once to auto-locate.
  useEffect(() => {
    if (hasCoords) return;
    const query = [addressLine, city].map((p) => p.trim()).filter(Boolean).join(', ');
    if (query.length < 5) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await searchLocations(query, { city: city || undefined, limit: 1 });
        if (!cancelled && results[0]) {
          setSearchValue(results[0]);
          onChange(results[0].lat.toFixed(6), results[0].lng.toFixed(6));
        }
      } catch {
        /* optional auto-locate — ignore failures */
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when address/city identity changes meaningfully
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressLine.trim(), city.trim()]);

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.75,
        borderRadius: 2,
        border: 1,
        borderColor: error ? 'error.main' : hasCoords ? 'success.light' : 'divider',
        bgcolor: (theme) => alpha(theme.palette.background.paper, 1),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <MyLocation fontSize="small" color={hasCoords ? 'success' : 'primary'} />
        <Typography variant="subtitle2" fontWeight={700}>
          Restaurant location{required ? ' *' : ''}
        </Typography>
        {hasCoords ? (
          <Chip size="small" color="success" label={`Pinned · ${lat!.toFixed(5)}, ${lng!.toFixed(5)}`} />
        ) : (
          <Chip size="small" color={error ? 'error' : 'default'} variant="outlined" label="Not pinned yet" />
        )}
      </Stack>

      <Alert
        severity={error ? 'error' : hasCoords ? 'success' : 'info'}
        icon={<Place fontSize="inherit" />}
        sx={{
          mb: 1.5,
          py: 0.75,
          color: 'text.primary',
          '& .MuiAlert-message': { color: 'text.primary', fontWeight: 500 },
          '& .MuiAlert-icon': { color: error ? 'error.main' : hasCoords ? 'success.main' : 'info.main' },
        }}
      >
        {helperText
          || (hasCoords
            ? 'Location selected — drag is not available; click the map or search to move the pin.'
            : 'Search an address, use Locate from address, or click the map to drop a pin.')}
      </Alert>

      <Stack spacing={1.25} sx={{ mb: 1.5 }}>
        <LocationSearchField
          label="Search location"
          placeholder="Search address, landmark, or area…"
          value={searchValue}
          onChange={(loc) => {
            if (loc) applyLocation(loc);
            else setSearchValue(null);
          }}
          bias={hasCoords ? { lat: lat!, lng: lng! } : null}
          city={city || undefined}
          required={required && !hasCoords}
          error={error && !hasCoords}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<MyLocation />}
          onClick={() => void locateFromAddress()}
          disabled={locating}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
        >
          {locating ? 'Locating…' : 'Locate from address'}
        </Button>
        {(locateError || (error && !hasCoords)) && (
          <Typography variant="body2" color="error.main" fontWeight={600}>
            {locateError || helperText || 'Pin a location before creating the restaurant'}
          </Typography>
        )}
      </Stack>

      <ZoneMapPanel
        height={240}
        center={center}
        zoom={hasCoords ? 15 : 11}
        pin={hasCoords ? { lat: lat!, lng: lng! } : null}
        onMapClick={(clickLat, clickLng) => setCoords(clickLat, clickLng)}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          label="Latitude"
          type="number"
          value={latitude}
          required={required}
          error={error && !hasCoords}
          onChange={(e) => onChange(e.target.value, longitude)}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
        <TextField
          size="small"
          label="Longitude"
          type="number"
          value={longitude}
          required={required}
          error={error && !hasCoords}
          onChange={(e) => onChange(latitude, e.target.value)}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
      </Stack>
    </Box>
  );
}
